import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// ---------- Global runtime error observability ----------
// Log estruturado para conseguirmos diagnosticar iOS Safari sem mascarar o erro.
// Não intercepta o overlay do dev-server — apenas garante que a mensagem real
// (não "Script error.") vá para o console.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    // eslint-disable-next-line no-console
    console.error("[unhandledrejection]", ev.reason);
  });
  window.addEventListener("error", (ev) => {
    // eslint-disable-next-line no-console
    console.error("[window.error]", {
      message: ev.message,
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
      error: ev.error,
    });
  });
}

// ---------- Service Worker: registrar + auto-recuperação ----------
// Contexto: usuários que instalaram versões antigas do PWA (flr-v3, fg-v4) podem
// estar servindo um index.html cacheado que aponta para bundles JS já removidos.
// Ao carregar a preview/produção, o load do JS pode falhar → iOS Safari reporta
// como "Script error." no window.onerror.
//
// Mitigação em duas frentes:
//   1) Ao ativar um novo SW (controllerchange), recarregar a página uma vez para
//      pegar os bundles novos servidos pelo fetch mais recente.
//   2) Se detectarmos um SW obsoleto (versão desconhecida), damos oportunidade ao
//      novo sw.js/{fg-v5} de assumir via skipWaiting/clients.claim já embutidos.
if ("serviceWorker" in navigator) {
  let reloadedOnce = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Só recarrega uma vez para evitar loops.
    if (reloadedOnce) return;
    reloadedOnce = true;
    try { window.location.reload(); } catch {}
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[sw] register failed", err);
      });

    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "NAVIGATE" && event.data?.url) {
        window.location.href = event.data.url;
      }
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
